import React, { useEffect, useState } from "react";
import { createMetric, getMetricById, listMetrics, updateMetric } from "../../services/MetricService";
import { getModelById } from "../../services/DataModelService";
import EditMetricDefinition from "../../components/configuration/EditMetricDefinition";
import showToast from "../../hooks/useCustomToast";
import { 
  AggregationType, 
  FilterOperator,   
  MetricType,
  PolarityEnum, 
  polarityEnumFromJSON,  
} from "build-ts/enums";
import { 
  Header, 
  ColumnInfo, 
  ModelInfo, 
  ObservationColumnProto, 
  FilterProto, 
  FormatProto,
  MetricProto, 
} from "build-ts/metadata";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { DateFilterCategory } from "utils/enum";
import { DateFilter } from "build-ts/relative_date";

interface EditMetricDefinitionContainerProps {
  createMetricFlag: boolean;
  modelInfo: ModelInfo | null;
  metricHeader: Header|null;
  onClose: ()=> void;

  // For saving the metric definition 
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
}
const EditMetricDefinitionContainer: React.FC<
  EditMetricDefinitionContainerProps
> = ({ createMetricFlag, modelInfo, metricHeader, onClose, triggerSubmit, setTriggerSubmit, onReloadKnowledgeGraph }) => {
  
  const [metricName, setMetricName] = useState<string>("");
  const [columnNames, setColumnNames] = useState<string[]>([]);
  const [metricHeaders, setMetricHeaders] = useState<Header[]>([]);
  const [selectedColumnValues, setSelectedColumnValues] = useState<string[]>([]);
  const [metricDescription, setMetricDescription] = useState<string>("");
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [selectedObservationColumn, setSelectedObservationColumn] =
    useState<Header | null>(null);
  const [selectedFilterColumn, setSelectedFilterColumn] =
    useState<ColumnInfo | null>(null);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | undefined>(AggregationType.SUM);
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | undefined>(FilterOperator.EQ);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedObservationColumns, setSelectedObservationColumns] = useState<
    ObservationColumnProto[]
  >([]);
  const [selectedFilterColumns, setSelectedFilterColumns] = useState<FilterProto[]>([]);
  const [polarity, setPolarity] = useState<PolarityEnum | undefined>(PolarityEnum.POSITIVE);
  const [currency, setCurrency] = useState<string|undefined>("USD");
  const [percentage, setPercentage] = useState<boolean>(false);
  const [suffix, setSuffix] = useState<string | undefined>("");
  const hasContent = selectedObservationColumns?.length > 0 || selectedFilterColumns?.length > 0;
  const [displayType, setDisplayType] = useState<string>("Regular");
  const [selectedMateriality, setSelectedMateriality] = useState<Header | null>(null);
  const [decimalPlaces, setDecimalPlaces] = useState<number | null>(0);
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  const [metricType, setMetricType] = useState<MetricType | undefined>(MetricType.ADDITIVE);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if(!createMetricFlag && metricHeader){
      const fetchMetricDefinition = async () => {
      const response = await getMetricById(metricHeader.id!, false);
        if (response !== null) {
          setMetricName(response?.header?.name!);
          setMetricDescription(response?.header?.description!);
          setSelectedObservationColumns(response?.definition?.observation_columns ?? []);
          setSelectedFilterColumns(response?.definition?.filters ?? []);
          setCurrency(response?.format?.currency_code);
          setDecimalPlaces(response?.format?.num_decimals ? response?.format?.num_decimals: 0);

          if(response?.material_column_header) {
            setSelectedMateriality(response.material_column_header);
          }
          if(response?.material) {
            setSelectedMateriality(response?.material?.header!);
          }
          if(response?.format?.percentage === true){
            setPercentage(true);
            setDisplayType("Percentage");
          } 
          else if(response?.format?.currency_code) setDisplayType("Currency");
          else if(response?.format?.suffix) setDisplayType("Regular");
          
          setSuffix(response?.format?.suffix);
          setPolarity(response?.polarity);
          if (response?.type !== undefined) {
            setMetricType(response.type);
          }
        }
      };
      fetchMetricDefinition();
    }
  }, []);

  useEffect(() => {
    const fetchColumnFromModelId = async () => {
      let response: ModelInfo | null = null;
      // Handle the conditional assignment
      if (!createMetricFlag && metricHeader) {
        response = await getModelById(metricHeader.owner_id!) || null;
      } else {
        response = modelInfo;
      }

      // Process the response if it exists
      if (response) {
        response.columns?.forEach((column: ColumnInfo) => {
          setColumns((prevColumns) => [...prevColumns, column]);
        });
      }
    };
    const fetchMetricHeaders = async () => {
      const response = await listMetrics("");
      setMetricHeaders(response);
    };
    fetchColumnFromModelId();
    fetchMetricHeaders();
  }, []);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMetricName(event.target.value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMetricDescription(event.target.value);
  };

  //function to handle select multiple values
  const handleToggle = (value: string) => {
    const currentIndex = selectedColumnValues.indexOf(value);
    let newSelected = [...selectedColumnValues];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedColumnValues(newSelected);
  };

  const handleDeleteObservationColumn = (indexToDelete: number) => {
    setSelectedObservationColumns((prevColumns) =>
      prevColumns.filter((_, index) => index !== indexToDelete)
    );
  };

  const handleDeleteFilterColumn = (indexToDelete: number) => {
    setSelectedFilterColumns((prevColumns) =>
      prevColumns.filter((_, index) => index !== indexToDelete)
    );
  };

  const handlePolarityChange = (value: string) => {
    setPolarity(polarityEnumFromJSON(value));
  };

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrency(event.target.value);
    setPercentage(false);
    setSuffix("");
  };

  const handlePercentageChange = (value: boolean) => {
    setPercentage(value);
    setCurrency("");
    setSuffix("");
  };

  const handleSuffixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSuffix(event.target.value);
    setCurrency("");
    setPercentage(false);
  };

  const handleMaterialColumn = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const materialColumnId = event.target.value;
    const metric: Header = metricHeaders && metricHeaders.find((c) => c.id === materialColumnId)!;
    setSelectedMateriality(metric);
  };

  const handleMetricTypeChange = (isRatio: boolean) => {
    setMetricType(isRatio ? MetricType.RATIO : MetricType.ADDITIVE);
  };

  const submit = async () => {
    const observationColumns: ObservationColumnProto[] =
      selectedObservationColumns &&
      selectedObservationColumns.map((observation) => {
        return {
          logical_column_header: {
            id: observation?.logical_column_header?.id,
          },
          aggregation: observation.aggregation,
          sort_by: observation.sort_by || undefined,
        };
      });
    const filters: FilterProto[] =
      selectedFilterColumns &&
      selectedFilterColumns.map((filter: FilterProto) => {
        return FilterProto.create({
          logical_column_header: {
            id: filter?.logical_column_header?.id || "",
          },
          operator: filter.operator || FilterOperator.UNRECOGNIZED,
          values: filter.values ? filter.values : [], 
        });
      });

    let format: FormatProto = {
      currency_code: currency,
      percentage: percentage,
      suffix: suffix,
      num_decimals: decimalPlaces? decimalPlaces: 0,
    };

    //empty checks on format 
    if(!format.currency_code){
      delete format.currency_code;
    }
    if(!format.suffix){
      delete format.suffix;
    }
    if(!percentage){
      delete format.percentage;
    }

    const formData: CreateOrUpdateMetrics_Request = {
      model_id: (!createMetricFlag) ? String(metricHeader?.owner_id!) : modelInfo?.header?.id!,
      metrics: [
        {
          header: { ...metricHeader, name: metricName, description: metricDescription },
          definition: {
            observation_columns: observationColumns,
            filters: filters,
          },
          format: format,
          type: metricType,
          polarity: polarity,
          material_column_header: selectedMateriality || undefined,
          material: selectedMateriality ? MetricProto.create({
            header: selectedMateriality,
          }) : undefined,
          edges: []
        },
      ],
      update_edges: false,
    };

    //empty checks on metrics
    formData.metrics.forEach(metric => {
      if(metric?.polarity === undefined) 
          delete metric.polarity;
      if(Object.keys(metric.format!).length === 0) 
         delete metric.format;
    });

    if(!createMetricFlag){
      try {
        // API call to create the metric
        const responseData = await updateMetric(formData);
        if(responseData){
          showToast("Metric definition is updated","" , "success");
          onClose();
          // Trigger reload of Knowledge Graph
          if (onReloadKnowledgeGraph) {
            onReloadKnowledgeGraph();
          }
        }
      } catch (error) {
        showToast("Metric definition is not updated", "", "error");
      }
    }
    else{
      try{
        if(!metricName.length){
          showToast("Metric name cannot be empty", "", "error");
          return;
        }
        const responseData = await createMetric(formData);
        if(responseData){
          showToast("Metric definition is created", "", "success");
          onClose();
          // Trigger reload of Knowledge Graph
          if (onReloadKnowledgeGraph) {
            onReloadKnowledgeGraph();
          }
        }     
      } catch (error){
        showToast("Metric definition is not updated", "", "error");
      }
    }
  };

  // Watch for triggerSubmit and call submit when triggered
  useEffect(() => {
    if (triggerSubmit) {
      submit();
      // Reset the trigger after submitting
      if (setTriggerSubmit) {
        setTriggerSubmit(false);
      }
    }
  }, [triggerSubmit]);

  return (
    <EditMetricDefinition
      metricName={metricName}
      metricDescription={metricDescription}
      selectedObservationColumn={selectedObservationColumn!}
      columns={columns}
      selectedAggregation={selectedAggregation}
      selectedFilterColumn={selectedFilterColumn!}
      selectedObservationColumns={selectedObservationColumns}
      selectedFilterColumns={selectedFilterColumns}
      selectedOperator={selectedOperator}
      inputValue={inputValue}
      handleNameChange={handleNameChange}
      handleDescriptionChange={handleDescriptionChange}
      handleDeleteObservationColumn={handleDeleteObservationColumn}
      handleDeleteFilterColumn={handleDeleteFilterColumn}
      polarity={polarity}
      metricType={metricType}
      handleMetricTypeChange={handleMetricTypeChange}
      submit={submit}
      currency={currency}
      suffix={suffix}
      createMetricFlag={createMetricFlag}
      handlePolarityChange={handlePolarityChange}
      handleCurrencyChange={handleCurrencyChange}
      handlePercentageChange={handlePercentageChange}
      handleSuffixChange={handleSuffixChange}
      hasContent={hasContent}
      setDisplayType={setDisplayType}
      displayType={displayType}
      columnNames={columnNames}
      selectedColumnValues={selectedColumnValues}
      handleToggle={handleToggle}
      handleMaterialColumn={handleMaterialColumn}
      selectedMateriality={selectedMateriality}
      metricHeaders={metricHeaders}
      decimalPlaces={decimalPlaces}
      setDecimalPlaces={setDecimalPlaces}
      setSelectedObservationColumn={setSelectedObservationColumn}
      setColumnNames={setColumnNames}
      setSelectedFilterColumn={setSelectedFilterColumn}
      setSelectedColumnValues={setSelectedColumnValues}
      setSelectedAggregation={setSelectedAggregation}
      setSelectedOperator={setSelectedOperator}
      setInputValue={setInputValue}
      setSelectedFilterColumns={setSelectedFilterColumns}
      setSelectedObservationColumns={setSelectedObservationColumns}
      setDateFilter={setDateFilter}
      setDateFilterCategory={setDateFilterCategory}
      dateFilter={dateFilter}
      dateFilterCategory={dateFilterCategory}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
    />
  );
};

export default EditMetricDefinitionContainer;
